import { Box } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MetaTags } from 'src/components/MetaTags/meta-tags';

import { Conversation } from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import { ConversationWindow } from 'src/pages/Conversations/conversation';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { useSelectedConversation } from 'src/pages/Conversations/use-selected-conversation';
import { GenericModalSet } from 'src/pages/Search/generic-modal-set';
import { TabType, useGlobalTabs, useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

export const ConversationExpanded = () => {
  const { showErrorAlert, showInfoAlert } = useNotifications();
  const { navigate } = useNavigateRoute();
  const location = useLocation();
  const { isMobile } = useScreenDimensions();
  const conversationExistedRef = useRef(false);
  const { openInTab, closeTabWith } = useGlobalTabs();

  const { recordId: friendlyConversationId } = useRouteParams(UrlParams.recordId);

  const customViewId = (location.state as { customViewId?: string })?.customViewId;

  const { customViews } = useCustomViews({
    entity: EntityNames.Conversation,
  });

  const customView = useMemo(
    () => (customViewId ? customViews.find((i) => i.customViewId === customViewId) : null),
    [customViews, customViewId],
  );

  const { selectedConversation, isLoading: isSelectedConversationLoading } =
    useSelectedConversation({
      conversationId: friendlyConversationId,
      includeAttachments: true,
    });

  if (selectedConversation && !conversationExistedRef.current) {
    conversationExistedRef.current = true;
  }

  // open expanded conversation in tab.
  useEffect(() => {
    if (!isSelectedConversationLoading && !!selectedConversation) {
      openInTab(
        { type: TabType.Conversation, entity: selectedConversation },
        { customViewId },
      );
    }
  }, [customViewId, selectedConversation, isSelectedConversationLoading, openInTab]);

  const { updateConversation } = useConversationUpdate();

  const onUpdate = useCallback(
    async (conversation: Conversation) => {
      await updateConversation(conversation);
    },
    [updateConversation],
  );

  const onCloseConversation = useCallback(() => {
    if (customView?.customViewId) {
      navigate(
        portalPaths.entity.selected(
          customView.entityName,
          customView.customViewId,
          !isMobile ? friendlyConversationId : undefined,
        ),
      );
    } else {
      navigate(
        portalPaths.entity.all({
          entityName: 'conversations',
          id: isMobile ? friendlyConversationId : '',
        }),
      );
    }
  }, [
    customView?.customViewId,
    customView?.entityName,
    friendlyConversationId,
    isMobile,
    navigate,
  ]);

  const onDeleteConversation = useCallback(() => {
    closeTabWith(
      (tab) =>
        tab.type === TabType.Conversation && tab.entity.friendlyId === friendlyConversationId,
    );
  }, [closeTabWith, friendlyConversationId]);

  useEffect(() => {
    if (!isSelectedConversationLoading && !selectedConversation) {
      if (conversationExistedRef.current) {
        showInfoAlert('Conversation was deleted');
      } else {
        showErrorAlert(
          `Conversation "${friendlyConversationId}" not found. You’ve been redirected to All Conversations.`,
        );
      }

      closeTabWith(
        (tab) =>
          tab.type === TabType.Conversation &&
          tab.entity.friendlyId === friendlyConversationId,
      );

      navigate(portalPaths.entity.all({ entityName: 'conversations' }));
    }
  }, [
    isSelectedConversationLoading,
    friendlyConversationId,
    selectedConversation,
    showErrorAlert,
    showInfoAlert,
    navigate,
    closeTabWith,
  ]);

  return selectedConversation ? (
    <MetaTags title={selectedConversation.subject}>
      <Box height="100%" position="relative">
        <GenericModalSet
          entityName={EntityNames.Conversation}
          selectedRecord={selectedConversation}
        />
        <EntityWindowWrapper>
          <ConversationWindow
            isConversationExpanded
            customViewId={customView?.customViewId}
            customViewName={customView?.name}
            onUpdate={onUpdate}
            conversation={selectedConversation}
            onCloseConversation={onCloseConversation}
            onDeleteConversation={onDeleteConversation}
          />
        </EntityWindowWrapper>
      </Box>
    </MetaTags>
  ) : null;
};
